import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import TrainerTour from '../TrainerTour';

/**
 * App shell: 240px sidebar + 1fr main column. The topbar is sticky inside
 * the main column. Children render directly under the main column;
 * pages opt into the standard padded container with the `.page` class.
 *
 * Routes that should NOT use the dashboard chrome (assessment, sharable
 * pages, the marketing pages, login/register) bypass this wrapper entirely
 * — those routes are rendered directly by `App.js` outside the shell.
 *
 * Mobile responsiveness: below 992px (matches the legacy `breakpoint='lg'`),
 * the sidebar becomes an off-canvas drawer. The topbar shows a hamburger
 * button that toggles the drawer; an opaque backdrop closes it on tap.
 * State is held here so both Sidebar and Topbar can read/write it without
 * a context.
 */
function AppShell({ children, auth, location }) {
  const path = location?.pathname || '';
  const isDashboardRoute = path.startsWith('/dashboard');
  const isAuthed = !!auth?.isAuthenticated;
  const useShell = isAuthed && isDashboardRoute;

  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Desktop icon-rail: collapse the sidebar to icons-only to reclaim horizontal
  // space for wide pages. User-controlled + persisted; `railPrefRef` holds that
  // saved preference so a route-driven auto-collapse (below) never clobbers it.
  const readRailPref = () => {
    try {
      return localStorage.getItem('nubod.sidebarRail') === '1';
    } catch (_) {
      return false;
    }
  };
  const railPrefRef = useRef(readRailPref());
  const [rail, setRail] = useState(railPrefRef.current);
  const toggleRail = () =>
    setRail((v) => {
      const next = !v;
      railPrefRef.current = next;
      try {
        localStorage.setItem('nubod.sidebarRail', next ? '1' : '0');
      } catch (_) {
        /* private-mode browsers can reject localStorage */
      }
      return next;
    });

  // Some wide pages auto-collapse the rail on entry and restore the saved
  // preference on exit. The landing-page builder is one.
  const routeWantsRail = /^\/dashboard\/admin\/challenges\/[^/]+\/landing/.test(
    path
  );
  useEffect(() => {
    setRail(routeWantsRail ? true : railPrefRef.current);
  }, [routeWantsRail]);

  // The rail is a DESKTOP affordance — on mobile (< 992px) the sidebar is an
  // off-canvas drawer, and the rail's hover-flyouts don't work on touch. So the
  // collapse only takes effect on desktop; the drawer always shows the full nav.
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 991px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 991px)');
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  const effectiveRail = rail && !isMobile;

  // Close the drawer whenever the route changes — otherwise tapping a nav
  // item leaves the drawer covering the new page.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [path]);

  if (!useShell) {
    return <>{children}</>;
  }

  return (
    <div className={`app-shell ${effectiveRail ? 'rail' : ''}`}>
      <Sidebar
        mobileOpen={mobileNavOpen}
        onCloseMobile={() => setMobileNavOpen(false)}
        collapsed={effectiveRail}
        onToggleCollapse={toggleRail}
      />
      {/* Backdrop only renders when the mobile drawer is open. CSS hides
          it on desktop (the .sidebar-backdrop rule is inside the mobile
          breakpoint), but we still gate the JSX so it doesn't capture
          clicks if someone shrinks the viewport with it off. */}
      <div
        className={`sidebar-backdrop ${mobileNavOpen ? 'visible' : ''}`}
        onClick={() => setMobileNavOpen(false)}
        aria-hidden="true"
      />
      <div className="app-shell-main">
        <Topbar onOpenMobileNav={() => setMobileNavOpen(true)} />
        {children}
      </div>
      {/* First-visit studio tour for coaches (replay: account menu). */}
      <TrainerTour isMobile={isMobile} setMobileNavOpen={setMobileNavOpen} />
    </div>
  );
}

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default withRouter(connect(mapStateToProps)(AppShell));
